import { insertPoint } from "@remirror/pm/transform";
import type { EditorView } from "@remirror/pm/view";
import {
  ExtensionImageTheme,
  ExtensionTag,
  NodeExtension,
  type NodeExtensionSpec,
  getTextSelection,
  isNumber,
  omitExtraAttributes,
} from "remirror";
import type { CommandFunction, ProsemirrorNode } from "remirror";

type FileAttrs = { src: string | null; name: string };

type FileUploadProgress = { file: File; progress: (progress: number) => void };

type FileExtensionOptions = {
  onClick?: (src: string | null, attrs: FileAttrs) => void;
  uploadHandler?: (
    files: FileUploadProgress[],
  ) => Array<Promise<FileAttrs> | FileAttrs>;
  extraAttributes?: Record<string, unknown>;
};

type FileReactComponentProps = { node: ProsemirrorNode; view: EditorView };

type ExtraAttributesHelper = {
  defaults: () => Record<string, unknown>;
  parse: (node: Node) => Record<string, unknown>;
  dom: (node: ProsemirrorNode) => Record<string, unknown>;
};

function createFilePlaceholder(): HTMLDivElement {
  const element = document.createElement("div");
  element.classList.add(ExtensionImageTheme.IMAGE_LOADER);
  return element;
}

export class FileExtension extends NodeExtension {
  name = "file";

  tags = [ExtensionTag.InlineNode];

  constructor(options: FileExtensionOptions = {}) {
    super({
      ...options,
      extraAttributes: {
        src: { default: null },
        name: { default: "" },
        ...options.extraAttributes,
      },
    });
  }

  ReactComponent = (props: FileReactComponentProps) => {
    const { src, name } = props.node.attrs as FileAttrs;
    const onClick = () => {
      if (!props.view.editable) {
        // @ts-expect-error remirror NodeExtension options
        this.options.onClick?.(src, { src, name });
      }
    };

    return (
      <span onClick={onClick}>
        <i className="icon file" />
        {name}
      </span>
    );
  };

  createNodeSpec(
    extra: ExtraAttributesHelper,
    override: Partial<NodeExtensionSpec> = {},
  ): NodeExtensionSpec {
    return {
      inline: true,
      content: "",
      attrs: {
        ...extra.defaults(),
        src: { default: null },
        name: { default: "" },
      },
      ...override,
      parseDOM: [
        {
          tag: 'span[data-ext="file"]',
          getAttrs: (node: Node) => {
            const element = node as HTMLElement;
            return {
              ...extra.parse(node),
              name:
                element.getAttribute("data-name") ||
                element.getAttribute("name") ||
                element.textContent ||
                "",
              src:
                element.getAttribute("data-url") || element.getAttribute("src"),
            };
          },
        },
        ...(override.parseDOM ?? []),
      ],
      toDOM: (node: ProsemirrorNode) => {
        const rest = omitExtraAttributes(node.attrs, extra);
        const domAttrs = extra.dom(node);
        return [
          "span",
          { ...domAttrs, ...rest, "data-ext": "file" },
          domAttrs.name,
        ];
      },
    };
  }

  insertFile(attributes: FileAttrs, selection?: { from: number; to: number }) {
    return (param: CommandFunction) => {
      const { tr, dispatch } = param;
      const { from, to } = getTextSelection(selection ?? tr.selection, tr.doc);
      // @ts-expect-error remirror node type create
      const node = this.type.create(attributes);
      dispatch(tr.replaceRangeWith(from, to, node));
      return true;
    };
  }

  createCommands() {
    return {
      insertFile: this.insertFile.bind(this),
      uploadFile:
        (
          value: Promise<FileAttrs> | FileAttrs,
          onElement?: (element: HTMLElement) => void,
        ) =>
        (props: CommandFunction) => {
          const { tr } = props;
          let pos = tr.selection.from;
          // @ts-expect-error remirror manager store
          return this.store
            .createPlaceholderCommand({
              promise: value,
              placeholder: {
                type: "widget",
                get pos() {
                  return pos;
                },
                createElement: (_view: EditorView, _newPos: number) => {
                  const element = createFilePlaceholder();
                  onElement?.(element);
                  return element;
                },
              },
              onSuccess: (
                file: FileAttrs,
                range: { from: number; to: number },
                commandProps: CommandFunction,
              ) => this.insertFile(file, range)(commandProps),
            })
            .validate(
              ({
                tr: newTr,
                dispatch,
              }: {
                tr: CommandFunction["tr"];
                dispatch?: CommandFunction["dispatch"];
              }) => {
                // @ts-expect-error remirror node type
                const insertPos = insertPoint(newTr.doc, pos, this.type);

                if (insertPos == null) {
                  return false;
                }

                pos = insertPos;

                if (!newTr.selection.empty) {
                  dispatch?.(newTr.deleteSelection());
                }

                return true;
              },
              "unshift",
            )
            .generateCommand()(props);
        },
    };
  }

  fileUploadFileHandler(
    files: File[],
    _event: Event | ClipboardEvent,
    pos?: number,
  ) {
    // @ts-expect-error remirror NodeExtension options
    const { uploadHandler } = this.options;
    if (!uploadHandler) {
      return false;
    }

    // @ts-expect-error remirror manager store
    const { commands, chain } = this.store;
    const filesWithProgress = files.map((file, index) => ({
      file,
      progress: (progress: number) => {
        commands.updatePlaceholder(files[index], progress);
      },
    }));

    const uploads = uploadHandler(filesWithProgress);

    if (isNumber(pos)) {
      chain.selectText(pos);
    }

    for (const upload of uploads) {
      chain.uploadFile(upload);
    }

    chain.run();

    return true;
  }

  createPasteRules() {
    return [
      {
        type: "file",
        fileHandler: (props: {
          type: string;
          files: File[];
          event: Event | ClipboardEvent;
          pos?: number;
        }) => {
          const position = props.type === "drop" ? props.pos : undefined;
          return this.fileUploadFileHandler(props.files, props.event, position);
        },
      },
    ];
  }
}

export const createFileExtension = (options: Array<FileExtensionOptions>) => {
  const fileOptions: FileExtensionOptions = {
    extraAttributes: { src: { default: null }, name: { default: "" } },
    ...options[0],
  };
  return new FileExtension(fileOptions);
};
